import { Component } from "react"
import { useMemo } from "react"
import PropTypes from "prop-types"
import { isEmpty, map } from "lodash"
import { CardContent, Paper, Table, TableHead, TableBody, TableRow, TableCell } from "@mui/material"
import { DATE } from "helpers/shapes"
import { computeCSVData } from "helpers/csv"
import { smallPercentageFormatter, percentageFormatter } from "helpers/formatters/numbers"
import { formatLongDate } from "helpers/formatters/dates"
import MultiLine from "components/charts/multi-line"
import Tooltip from "components/analyze-tooltip"
import HighestLowestAttribution from "components/highest-lowest-attribution"
import GraphTopBar from "components/graph-top-bar"
import ExcelDownload from "components/excel-download"
import DetailRow from "./components/detail-row"
import NoContributionsRow from "components/contributors-table-content/components/NoContributionsRow"
import { useThemeChartColors, applyChartColorsToItems } from "hooks/use-theme-chart-colors"
import "./style.scss"

export class SecurityProfilePerformance extends Component {
  static propTypes = {
    date: DATE,
    startDate: DATE,
    endDate: DATE,
    setDate: PropTypes.func.isRequired,
    focusChartLine: PropTypes.func.isRequired,
    blurChartLine: PropTypes.func.isRequired,
    focusedChartLine: PropTypes.string,
    isPeriodIntraday: PropTypes.bool,
    toggle: PropTypes.func.isRequired,
    data: PropTypes.array.isRequired,
    items: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
      disabled: PropTypes.bool.isRequired,
    })).isRequired,
    options: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
    })).isRequired,
    displayType: PropTypes.string.isRequired,
    setDisplayType: PropTypes.func.isRequired,
    openFactorProfile: PropTypes.func.isRequired,
    details: PropTypes.array.isRequired,
    performanceAttribution: PropTypes.object.isRequired,
    maxFactors: PropTypes.number.isRequired,
    toggleOnly: PropTypes.func.isRequired,
    only: PropTypes.string,
    trackCSVDownload: PropTypes.func.isRequired,
    fileName: PropTypes.string.isRequired,
  }

  onTabChanged = (_, id) => {
    const { setDisplayType } = this.props
    setDisplayType(id)
  }

  render() {
    const {
      options,
      details,
      date,
      setDate,
      startDate,
      endDate,
      toggle,
      displayType,
      focusedChartLine,
      focusChartLine,
      blurChartLine,
      data,
      items,
      performanceAttribution,
      maxFactors,
      openFactorProfile,
      toggleOnly,
      only,
      fileName,
      trackCSVDownload,
      isPeriodIntraday,
    } = this.props

    const hasData = !isEmpty(data)

    return <CardContent className="card-content modules_security-profile-performance">
      <Paper className="content">
        <div className="graph">
          {hasData && <GraphTopBar {...{
            value: displayType,
            tabs: map(options, ({ id, label, value }) => ({
              key: id,
              value: id,
              label: <><div>{label}</div><div>{percentageFormatter(value)}</div></>,
            })),
            onTabChanged: this.onTabChanged,
            actions: [<ExcelDownload key="excel-download" data={computeCSVData(data, items, { percents: true })} fileName={fileName} track={trackCSVDownload} />],
          }}
          />}
          <MultiLine
            {...{
              tooltip: <Tooltip data={data} items={items} focusedChartLine={focusedChartLine} formatter={(v) => v === "--" ? v : smallPercentageFormatter(v)} isOneDayMode={isPeriodIntraday} />,
              tickFormat: smallPercentageFormatter,
              startDate,
              date,
              setDate,
              endDate,
              isPeriodIntraday,
              data,
              onlyItem: only,
              items,
              toggle,
              focusChartLine,
              blurChartLine,
              focusedChartLine,
              toggleOnly,
            }}
          />
        </div>
        <div className="details">
          <h4 className="header">As of {formatLongDate(date)}</h4>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width="75%" />
                <TableCell align="right">% Perf</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {hasData
                ? map(details, (value, index) =>
                  <DetailRow key={value.id} value={value} index={index} openFactorProfile={openFactorProfile} />,
                  )
                : <NoContributionsRow colSpan={2} />}
            </TableBody>
          </Table>
        </div>
      </Paper>
      {hasData && <HighestLowestAttribution
        date={date}
        highestTitle="Winners"
        lowestTitle="Losers"
        attribution={performanceAttribution}
        maxFactors={maxFactors}
        openFactorProfile={openFactorProfile}
      />}
    </CardContent>
  }
}

function SecurityProfilePerformanceWithTheme(props) {
  const chartColors = useThemeChartColors()
  const items = useMemo(() => applyChartColorsToItems(props.items, chartColors), [props.items, chartColors])
  return <SecurityProfilePerformance {...props} items={items} />
}

SecurityProfilePerformanceWithTheme.propTypes = {
  ...SecurityProfilePerformance.propTypes,
}

export default SecurityProfilePerformanceWithTheme
